/** Target max size for resized images (~200–300 kB like mobile). Enforced by lowering JPEG quality. */
export const DEFAULT_MAX_BYTES = 300 * 1024; // 300 kB

/** Reject originals larger than this to avoid processing huge files. */
export const MAX_ORIGINAL_BYTES = 15 * 1024 * 1024; // 15 MB

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
            "image/jpeg",
            quality
        );
    });
}

/**
 * Resize an image to fit within maxWidth x maxHeight (keeps aspect ratio),
 * then compress to JPEG so the result does not exceed maxBytes.
 * If original is over MAX_ORIGINAL_BYTES, returns null (caller should show error).
 */
export async function resizeImageFile(
    file: File,
    maxWidth: number,
    maxHeight: number,
    maxBytes: number = DEFAULT_MAX_BYTES
): Promise<File | null> {
    if (file.size > MAX_ORIGINAL_BYTES) return null;

    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = async () => {
            URL.revokeObjectURL(url);

            let width = img.naturalWidth;
            let height = img.naturalHeight;
            const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                resolve(file);
                return;
            }
            ctx.drawImage(img, 0, 0, width, height);

            const name = file.name.replace(/\.[^.]+$/, "") || "image";
            const qualities = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.35, 0.3];

            for (const q of qualities) {
                const blob = await canvasToBlob(canvas, q);
                if (blob.size <= maxBytes) {
                    resolve(new File([blob], `${name}.jpg`, { type: "image/jpeg" }));
                    return;
                }
            }

            const blob = await canvasToBlob(canvas, 0.3);
            resolve(new File([blob], `${name}.jpg`, { type: "image/jpeg" }));
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
        };

        img.src = url;
    });
}
